import * as React from "react";

import "./social-dashboard-reschedule-modal.scss";

import {cs}                                  from "@Common/react/chain-services";
import {consumeContext}                      from "@Common/react/context";
import {convertState}                        from "@Common/react/convert-state";
import {Form2}                               from "@Common/react/cs-form-2/form2";
import {scopedState}                         from "@Common/react/scoped-state";
import {UseState}                            from "@Common/react/use-state";
import {DatePicker2}                         from "@Common/ui-components/date-picker-2/date-picker-2";
import {ModalService3}                       from "@Common/ui-components/modal-service-3/modal-service-3";
import {TimePicker2}                         from "@Common/ui-components/time-picker-2/time-picker-2";
import {parseDateFormat}                     from "@Common/utils/date/parse-date-format";
import {addDate, today}                      from "@Common/utils/date-object";
import {IMAGE_SOURCE, utcTimeToLocalTime}    from "../social-dashboard-card";

const TITLE_RESCHEDULE = "Change Scheduled Post"
const ERROR_MESSAGE_SCHEDULE = "The scheduled date and time must be in the future."
const DAYS_IN_FUTURE_SCHEDULE_MAX = 14

export const SocialDashboardRescheduleModal = ({post, data, next: rootNext}) => cs(
    ["localDateTime", ({}, next) => next(utcTimeToLocalTime(post?.dateScheduled?.date))],
    ["dataScheduled", ({localDateTime}, next) => UseState({
        getInitValue: () => ({
            schedule: {
                enabled: true,
                date: df.formatDate({
                    year: localDateTime.getFullYear(), 
                    month: cappedMonth(localDateTime),
                    day: localDateTime.getDate(),
                }),
                time: `${localDateTime.getHours()}:${localDateTime.getMinutes()}`,
            },
        }),
        next,
    })],
    ["form", ({dataScheduled}, next) => Form2({
        data: dataScheduled,
        showErrors: true,
        fields: {          
            schedule: [
                {
                    validate: (v) => !v?.enabled || (v.date && v.time),
                }
            ],
        },
        next,
    })],
    ["schedulingError", (_, next) => UseState({
        initValue: false,
        next,
    })],
    ["modal", ({schedulingError}, next) => ModalService3({
        xdQaName: "Reschedule Modal",
        width: 360,
        onPassiveClose: () => schedulingError.onChange(false),
        rootNext,
        next, 
    })],
    consumeContext('apis'),  
    ({form, modal, schedulingError, apis}) => {
        const field = form.field("schedule")
        const stateDate = scopedState(field.state, ["date"])
        const stateTime = scopedState(field.state, ["time"])
        const scheduled = scheduledDateTime(field.state.value.date, field.state.value.time)

        return (
        <div className="social-dashboard-reschedule-modal-P2O5" xd-qa-type="modal" xd-qa-name="Reschedule">
            <div className="social-dashboard-reschedule-header" >
                {TITLE_RESCHEDULE}
            </div>
            <div className="social-dashboard-reschedule-content">
                <div className="social-dashboard-reschedule-post">
                    <div className="social-dashboard-reschedule-post-img">
                        <img src={IMAGE_SOURCE[post['shareSite']]} alt={post['shareSite']} />
                    </div>
           
                    <div className="social-dashboard-reschedule-post-message">
                        {post['shareBody']['message']}
                    </div>
                </div>
                {            
                    DatePicker2({
                        state: convertState(stateDate, {
                            forward: (dStr) => dStr && df.parseDate(dStr),
                            backward: (d) => d && df.formatDate(d),
                        }),
                        minDate: today(),
                        maxDate: addDate(today(), DAYS_IN_FUTURE_SCHEDULE_MAX),
                        dateFormat: df,
                        xdQaName: "Date",
                        className: "social-dashboard-reschedule-date",                        
                        onClick: () => schedulingError.onChange(false),
                    })
                }
                {
                    TimePicker2({
                        state: stateTime,
                        className: "social-dashboard-reschedule-time",
                        onClick: () => schedulingError.onChange(false),
                        hasError: schedulingError.value,
                    })
                }
                <div className="social-dashboard-reschedule-error-message">
                    {
                        schedulingError.value
                        ?
                        ERROR_MESSAGE_SCHEDULE
                        :
                        null
                    }
                </div>
            </div>
            <div className="social-dashboard-reschedule-controls">
                <button className="social-dashboard-reschedule-controls-confirm"
                     onClick={async (e) => {
                        e.stopPropagation()
                        
                        if(!scheduledIsInFuture(scheduled)) {
                            schedulingError.onChange(true)
                            return
                        }                      
                                                
                        const share_ref_id = post.shareRefId
                        const utc = scheduledTimeToUTCTime(scheduled)
                        const date_scheduled = formattedDateTime(utc)
                        const share_location = post.shareLocation
                        const entity_type = post.sharerEntityType
                        const result = await apis.next.social.reschedulePost({share_ref_id, date_scheduled, share_location, entity_type})
                        if(result.action) {
                            data.reload()
                        }
                        modal.close()
                    }}
                >
                    OK
                </button>
                <button className="social-dashboard-reschedule-controls-cancel"
                    onClick={(e) => {
                        e.stopPropagation()
                        modal.close()
                    }}
                >
                    Cancel
                </button>                
            </div>
        </div>
    )},
);

const df = parseDateFormat("yyyy-mm-dd")

const scheduledDateTime = (date, time) => {
    const scheduled = new Date()

    const dateParts = date.split("-")
    scheduled.setFullYear(dateParts[0])
    scheduled.setMonth((dateParts[1] - 1))
    scheduled.setDate(dateParts[2])

    const timeParts = time.split(":")
    scheduled.setHours(timeParts[0])
    scheduled.setMinutes(timeParts[1])    

    return scheduled
}

const scheduledIsInFuture = (scheduled) => {
    const now = new Date(Date.now())
    
    return (scheduled > now)
}

const scheduledTimeToUTCTime = (scheduled) => {
    const millisecondsPerMinute = 60000
    const offsetTimezone = new Date().getTimezoneOffset() * millisecondsPerMinute  

    return (new Date(scheduled.getTime() + offsetTimezone))
}

const formattedDateTime = (dateTime) => {
    const month = twoDigit(dateTime.getMonth() + 1)    
    const day = twoDigit(dateTime.getDate())
    const date = `${dateTime.getFullYear()}-${month}-${day}`

    const minutes = twoDigit(dateTime.getMinutes())
    const time = `${dateTime.getHours()}:${minutes}:00`

    return `${date} ${time}`
}

const twoDigit = (value) => {
    return (value < 10 ? `0${value}` : value)
}

const cappedMonth = (date) => {
    const JANUARY = `01`
    const DECEMBER = `12`

    const month = date.getMonth() + 1

    if(month < JANUARY) {
        return JANUARY
    }

    if(month > DECEMBER) {
        return DECEMBER
    }

    return month
}
